import type { Patient, Reference } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { FirstMatchDecorator } from '#src/components/rich-text-editor/ObservationNode/FirstMatchDecorator.ts'
import { ObservationNodeInlineDisplay } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeInlineDisplay.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function ObservationNodeNumber({
  attributes,
  updateAttributes,
  participant,
  onReferencePersisted,
  disabled,
}: {
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  participant: Reference<Patient> | undefined
  onReferencePersisted?: () => void
  disabled?: boolean
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const { data: observation } = useRead(
    ExtendedObservation,
    attributes.reference
  )

  const createObservationPromiseRef =
    useRef<Promise<ExtendedObservation> | null>(null)

  const createMutation = useCreateMutation<ExtendedObservation>()
  const practitioner = useCurrentPractitioner()
  const { mutateAsync: patchObservation } =
    usePatchMutation<ExtendedObservation>()

  const updateObservation = useCallback(
    async (observation: ExtendedObservation, html: string) => {
      const value = extractNumberFromHtml(html)
      try {
        await patchObservation({
          instance: observation,
          mutateFn: (o) => {
            o.updateValueQuantityValue(value ?? undefined)
            o.setNoteFirst(html, practitioner.reference)
          },
        })
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? `Failed to update ${attributes.codingDisplay.toLowerCase()}: ${error.message}`
            : `Failed to update ${attributes.codingDisplay.toLowerCase()}`
        toast.error(errorMessage)
        throw error
      }
    },
    [patchObservation, practitioner.reference, attributes.codingDisplay]
  )

  const debouncedOnChange = useDebouncedCallback(async (html) => {
    if (!attributes.reference) {
      if (createObservationPromiseRef.current) {
        // Wait for the existing createObservationPromiseRef to finish before proceeding
        const createdObservation = await createObservationPromiseRef.current
        await updateObservation(createdObservation, html)
      } else {
        invariant(attributes.unit, 'unit is required')
        const value = extractNumberFromHtml(html)
        if (value === undefined) {
          toast.error(
            `Enter a number for ${attributes.codingDisplay.toLowerCase()}.`
          )
          return
        }
        createObservationPromiseRef.current = createMutation.mutateAsync(
          ExtendedObservation.fromValueQuantity({
            value,
            unit: attributes.unit,
            code: codeableConcept({
              code: attributes.code,
              system: attributes.system,
              display: attributes.codingDisplay,
            }),
            participant,
            practitioner: practitioner.reference,
            note: html,
          })
        )
        let createdObservation: ExtendedObservation
        try {
          createdObservation = await createObservationPromiseRef.current
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? `Failed to submit ${attributes.codingDisplay.toLowerCase()}: ${error.message}`
              : `Failed to submit ${attributes.codingDisplay.toLowerCase()}`
          toast.error(errorMessage)
          throw error
        } finally {
          createObservationPromiseRef.current = null
        }
        updateAttributes({
          reference: createdObservation.reference.reference,
        })
        onReferencePersisted?.()
      }
    } else {
      invariant(observation, 'observation is required')
      await updateObservation(observation, html)
    }
  }, 500) // Reduced from 2500ms to 500ms to mitigate race condition where users submit timeline notes before observation reference is created. Still debounced to avoid excessive version history entries, and we flush on blur as well.

  const handleAutofocused = useCallback(() => {
    updateAttributes({ autofocus: false })
  }, [updateAttributes])

  return (
    <ObservationNodeInlineDisplay
      value={
        observation?.noteFirstText ??
        observation?.valueQuantity?.toString() ??
        ''
      }
      observationCode={attributes.code}
      observationDisplay={attributes.codingDisplay}
      debouncedOnChange={debouncedOnChange}
      additionalExtensions={additionalExtensions}
      autofocus={attributes.autofocus}
      onAutofocused={handleAutofocused}
      disabled={disabled || isReadOnly}
      noValue={disabled && !attributes.reference}
    />
  )
}

const NUMBER_REGEX = /-?\d+(?:\.\d+)?/

const additionalExtensions = [
  FirstMatchDecorator.configure({
    regex: NUMBER_REGEX,
    className: 'font-mono tabular-nums text-foreground',
  }),
]

function extractNumberFromHtml(html: string): number | undefined {
  // Strip HTML tags and normalize whitespace
  const text = html
    .replace(/<[^>]+>/g, ' ') // remove tags
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim()

  const match = text.match(NUMBER_REGEX)

  if (!match) {
    return undefined
  }

  return Number(match[0])
}
